import { Chord } from "@tonaljs/tonal";
import { compactRootMidi, midiForPitchNear, mod, noteNameForPc, notePc } from "../js/music/notes.js";
import { flashPianoKey, highlightPiano, renderPiano } from "../js/music/piano.js";
import { flashGuitarPitch, renderGuitarDiagram, voicingFromFingeringResult } from "../js/music/guitar.js";
import { playSoundfontPreview } from "../js/music/soundfont-previews.js";
import { normalizeSoundfontId, playChordMidis, playMidi, renderSoundBankOptions } from "../js/music/soundfonts.js";

const SETTINGS_STORAGE_KEY = "bardsCorner.chords.settings.v1";
const KEYS = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const FLAT_KEYS = new Set(["Db", "Eb", "Gb", "Ab", "Bb"]);
const ALLOWED_CHORD_TYPES = [
  "Major", "Minor", "7", "5", "dim", "dim7", "aug", "sus2", "sus4", "maj7",
  "m7", "7sus4", "maj9", "maj11", "maj13", "maj9#11", "maj13#11", "add9",
  "6add9", "maj7b5", "maj7#5", "m6", "m9", "m11", "m13", "madd9", "m6add9",
  "mmaj7", "mmaj9", "m7b5", "m7#5", "6", "9", "11", "13", "7b5", "7#5",
  "7b9", "7#9", "7(b5,b9)", "7(b5,#9)", "7(#5,b9)", "7(#5,#9)", "9b5", "9#5",
  "13#11", "13b9", "11b9", "sus2sus4", "b5",
  "7#11", "7b13", "7alt", "9sus4", "13sus4", "mmaj11", "mmaj13", "add11",
  "add13", "madd11", "6/9",
];
const PRIMARY_CHORD_TYPES = ["Major", "Minor", "7", "m7", "maj7", "sus2", "sus4", "9", "11", "13"];
const TYPE_INTERVALS = {
  Major: [0, 4, 7],
  Minor: [0, 3, 7],
  7: [0, 4, 7, 10],
  5: [0, 7],
  dim: [0, 3, 6],
  dim7: [0, 3, 6, 9],
  aug: [0, 4, 8],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  maj7: [0, 4, 7, 11],
  m7: [0, 3, 7, 10],
  "7sus4": [0, 5, 7, 10],
  maj9: [0, 4, 7, 11, 14],
  maj11: [0, 4, 7, 11, 14, 17],
  maj13: [0, 4, 7, 11, 14, 21],
  "maj9#11": [0, 4, 7, 11, 14, 18],
  "maj13#11": [0, 4, 7, 11, 14, 18, 21],
  add9: [0, 4, 7, 14],
  "6add9": [0, 4, 7, 9, 14],
  maj7b5: [0, 4, 6, 11],
  "maj7#5": [0, 4, 8, 11],
  m6: [0, 3, 7, 9],
  m9: [0, 3, 7, 10, 14],
  m11: [0, 3, 7, 10, 14, 17],
  m13: [0, 3, 7, 10, 14, 17, 21],
  madd9: [0, 3, 7, 14],
  m6add9: [0, 3, 7, 9, 14],
  mmaj7: [0, 3, 7, 11],
  mmaj9: [0, 3, 7, 11, 14],
  m7b5: [0, 3, 6, 10],
  "m7#5": [0, 3, 8, 10],
  6: [0, 4, 7, 9],
  9: [0, 4, 7, 10, 14],
  11: [0, 4, 7, 10, 14, 17],
  13: [0, 4, 7, 10, 14, 21],
  "7b5": [0, 4, 6, 10],
  "7#5": [0, 4, 8, 10],
  "7b9": [0, 4, 7, 10, 13],
  "7#9": [0, 4, 7, 10, 15],
  "7(b5,b9)": [0, 4, 6, 10, 13],
  "7(b5,#9)": [0, 4, 6, 10, 15],
  "7(#5,b9)": [0, 4, 8, 10, 13],
  "7(#5,#9)": [0, 4, 8, 10, 15],
  "9b5": [0, 4, 6, 10, 14],
  "9#5": [0, 4, 8, 10, 14],
  "13#11": [0, 4, 7, 10, 14, 18, 21],
  "13b9": [0, 4, 7, 10, 13, 21],
  "11b9": [0, 4, 7, 10, 13, 17],
  sus2sus4: [0, 2, 5, 7],
  b5: [0, 4, 6],
  "7#11": [0, 4, 7, 10, 18],
  "7b13": [0, 4, 7, 10, 20],
  "7alt": [0, 4, 10, 13, 15, 20],
  "9sus4": [0, 5, 7, 10, 14],
  "13sus4": [0, 5, 7, 10, 14, 21],
  mmaj11: [0, 3, 7, 11, 14, 17],
  mmaj13: [0, 3, 7, 11, 14, 21],
  add11: [0, 4, 7, 17],
  add13: [0, 4, 7, 21],
  madd11: [0, 3, 7, 17],
  "6/9": [0, 4, 7, 9, 14],
};
const DEFAULT_STATE = {
  root: "C",
  type: "Major",
  voicingIndex: 0,
  soundBank: "acoustic_grand_piano",
};
const state = { ...DEFAULT_STATE, showAdvancedTypes: false, favorites: [], favoriteLoading: false };

function $(id) {
  return document.getElementById(id);
}

function makeId(prefix) {
  if (window.crypto?.randomUUID) {
    return `${prefix}_${window.crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function apiBase() {
  const host = window.location.hostname;
  if (host === "bards-corner.com" || host === "www.bards-corner.com") {
    return "https://bards-corner.com/api";
  }
  if (host === "localhost" || host === "127.0.0.1" || window.location.protocol === "file:") {
    return "https://dev.bards-corner.com/api";
  }
  return `${window.location.origin}/api`;
}

async function requestJson(path, options = {}) {
  const response = await fetch(`${apiBase()}${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.error || "Request failed.");
  }
  return body;
}

function tonalSuffixForType(type) {
  if (type === "Major") {
    return "";
  }
  if (type === "Minor") {
    return "m";
  }
  return type;
}

function availableChordTypes() {
  const guitarTypes = window.GUITAR_FINGERING_DATA?.chords;
  if (!Array.isArray(guitarTypes)) {
    return ALLOWED_CHORD_TYPES;
  }
  const available = new Set(guitarTypes);
  return ALLOWED_CHORD_TYPES.filter((type) => available.has(type));
}

function isAllowedType(type) {
  return availableChordTypes().includes(type);
}

function typeFromSavedChord(quality, chordTypeParam) {
  if (isAllowedType(chordTypeParam)) {
    return chordTypeParam;
  }
  if (chordTypeParam === "Major" || quality === "major" || quality === "Major") {
    return "Major";
  }
  if (chordTypeParam === "Minor" || quality === "minor" || quality === "Minor") {
    return "Minor";
  }
  const savedTypeMap = {
    power: "5",
    dim: "dim",
    diminished: "dim",
    aug: "aug",
    augmented: "aug",
    sus2: "sus2",
    sus4: "sus4",
  };
  return savedTypeMap[quality] || DEFAULT_STATE.type;
}

function notesFromIntervals(type) {
  const rootPc = notePc(state.root);
  const intervals = TYPE_INTERVALS[type] || TYPE_INTERVALS.Major;
  return intervals.map((semitones) => noteNameForPc(rootPc + semitones, preferFlats()));
}

function currentChord() {
  const type = state.type;
  const symbol = `${state.root}${tonalSuffixForType(type)}`;
  const chord = Chord.get(symbol);
  const rootMidi = compactRootMidi(state.root);
  const notes = chord.notes.length ? chord.notes : notesFromIntervals(type);
  const midis = notes.map((note) => midiForPitchNear(note, rootMidi)).filter(Number.isInteger);

  return {
    type,
    symbol,
    normalizedSymbol: chord.symbol || symbol,
    name: chord.name || `${state.root} ${type}`,
    notes,
    midis,
    intervals: chord.intervals || [],
    valid: chord.notes.length > 0,
  };
}

function fingeringData() {
  return window.GUITAR_FINGERING_DATA || null;
}

function fingeringResults(chord = currentChord()) {
  const data = fingeringData();
  const rootIndex = data?.roots?.indexOf(state.root.replace("Db", "C#").replace("Eb", "D#").replace("Gb", "F#").replace("Ab", "G#").replace("Bb", "A#"));
  const typeIndex = data?.chords?.indexOf(chord.type);
  if (!data || rootIndex < 0 || typeIndex < 0) {
    return [];
  }
  return data.voicings?.[rootIndex]?.[typeIndex]?.results || [];
}

function currentVoicing(chord = currentChord()) {
  const results = fingeringResults(chord);
  if (!results.length) {
    return null;
  }
  const index = mod(state.voicingIndex, results.length);
  return voicingFromFingeringResult(results[index]);
}

function preferFlats() {
  return FLAT_KEYS.has(state.root);
}

function labelNotes(notes) {
  return notes.map((note) => noteNameForPc(notePc(note), preferFlats())).join(" · ");
}

function renderKeySelect() {
  $("keySelect").replaceChildren(...KEYS.map((key) => {
    const option = document.createElement("option");
    option.value = key;
    option.textContent = key;
    return option;
  }));
}

function renderTypes() {
  const available = availableChordTypes();
  const primaryTypes = PRIMARY_CHORD_TYPES.filter((type) => available.includes(type));
  const types = state.showAdvancedTypes
    ? [...primaryTypes, ...available.filter((type) => !PRIMARY_CHORD_TYPES.includes(type))]
    : primaryTypes;
  $("typeList").replaceChildren(...types.map((type) => {
    const button = document.createElement("button");
    button.className = `type-button${type === state.type ? " active" : ""}`;
    button.type = "button";
    button.textContent = type;
    button.setAttribute("aria-pressed", String(type === state.type));
    button.addEventListener("click", () => {
      state.type = type;
      state.voicingIndex = 0;
      updateChord({ play: true });
    });
    return button;
  }));
  $("advancedTypesButton").setAttribute("aria-expanded", String(state.showAdvancedTypes));
  $("advancedTypesButton").textContent = state.showAdvancedTypes ? "Basic" : "Advanced";
}

function renderVoicingControls(chord) {
  const count = fingeringResults(chord).length;
  $("prevVoicingButton").disabled = count < 2;
  $("nextVoicingButton").disabled = count < 2;
  $("voicingLabel").textContent = count ? `Voicing ${mod(state.voicingIndex, count) + 1} of ${count}` : "No voicing";
}

function renderChord() {
  const chord = currentChord();
  const piano = $("piano");
  $("keySelect").value = state.root;
  $("soundBankSelect").value = state.soundBank;
  renderTypes();
  $("chordName").textContent = chord.normalizedSymbol;
  $("chordNotes").textContent = labelNotes(chord.notes);
  $("instrumentTitle").textContent = chord.name;
  $("validatedLabel").textContent = chord.valid ? "Validated with Tonal" : "Fallback chord";
  highlightPiano(piano, chord.midis, chord.midis[0]);
  renderGuitarDiagram($("guitarDiagram"), currentVoicing(chord), {
    root: state.root,
    flats: preferFlats(),
    showFingers: true,
    onNoteClick: ({ midi, pitch, element }) => {
      flashPianoKey(piano, pitch);
      flashGuitarPitch($("guitarDiagram"), pitch);
      playSingleMidi(midi, element);
    },
  });
  renderVoicingControls(chord);
}

function saveSettings() {
  try {
    window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify({
      version: 1,
      root: state.root,
      type: state.type,
      soundBank: state.soundBank,
    }));
  } catch {
    // Local settings are convenience only.
  }
}

function loadSettings() {
  try {
    const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    const settings = raw ? JSON.parse(raw) : {};
    state.root = KEYS.includes(settings.root) ? settings.root : DEFAULT_STATE.root;
    state.type = isAllowedType(settings.type)
      ? settings.type
      : typeFromSavedChord(settings.quality, settings.chordType);
    state.soundBank = normalizeSoundfontId(settings.soundBank);
  } catch {
    Object.assign(state, DEFAULT_STATE);
  }
}

function updateChord({ play = false } = {}) {
  saveSettings();
  renderChord();
  if (play) {
    playCurrentChord();
  }
}

async function playCurrentChord() {
  const chord = currentChord();
  try {
    chord.midis.forEach((midi) => flashPianoKey($("piano"), midi));
    await playChordMidis(chord.midis, { soundBank: state.soundBank, playStyle: "strum" });
  } catch (error) {
    window.alert(error.message || "Could not load that sound.");
  }
}

async function playSingleMidi(midi, element) {
  try {
    await playMidi(midi, { soundBank: state.soundBank, triggerElement: element });
  } catch (error) {
    window.alert(error.message || "Could not load that sound.");
  }
}

function setFavoritesMessage(kind, text) {
  $("favoritesMessage").className = `alert alert-${kind} mb-0`;
  $("favoritesMessage").textContent = text;
}

function clearFavoritesMessage() {
  $("favoritesMessage").className = "alert d-none mb-0";
  $("favoritesMessage").textContent = "";
}

function favoritePayload() {
  const chord = currentChord();
  return {
    favoriteId: makeId("chord"),
    root: state.root,
    quality: chord.type,
    chordType: chord.type,
    extensions: [],
    chordSymbol: chord.normalizedSymbol,
    notes: chord.notes,
    soundBank: state.soundBank,
  };
}

async function saveFavorite() {
  $("saveFavoriteButton").disabled = true;
  try {
    await requestJson("/chords/favorites", {
      method: "POST",
      body: JSON.stringify(favoritePayload()),
    });
    setFavoritesMessage("success", "Favorite saved.");
    await loadFavorites();
  } catch (error) {
    setFavoritesMessage("danger", error.message);
  } finally {
    $("saveFavoriteButton").disabled = false;
  }
}

async function deleteFavorite(id, button) {
  button.disabled = true;
  try {
    await requestJson(`/chords/favorites/${encodeURIComponent(id)}`, { method: "DELETE" });
    await loadFavorites();
  } catch (error) {
    button.disabled = false;
    setFavoritesMessage("danger", error.message);
  }
}

function applyFavorite(favorite, { play = true } = {}) {
  state.root = KEYS.includes(favorite.root) ? favorite.root : state.root;
  state.type = isAllowedType(favorite.chordType)
    ? favorite.chordType
    : typeFromSavedChord(favorite.quality, favorite.chordType);
  state.soundBank = normalizeSoundfontId(favorite.soundBank, state.soundBank);
  state.voicingIndex = 0;
  updateChord({ play });
}

function renderFavorites() {
  const list = $("favoritesList");
  list.replaceChildren();

  if (!state.favorites.length) {
    setFavoritesMessage("secondary", "No favorite chords yet.");
    return;
  }

  clearFavoritesMessage();
  state.favorites.forEach((favorite) => {
    const row = document.createElement("div");
    row.className = "favorite-row";
    const detail = document.createElement("div");
    const title = document.createElement("div");
    title.className = "favorite-title";
    title.textContent = favorite.chordSymbol;
    const meta = document.createElement("div");
    meta.className = "favorite-meta";
    meta.textContent = `${(favorite.notes || []).join(" · ")} | ${favorite.soundBank || "default"}`;
    detail.append(title, meta);

    const actions = document.createElement("div");
    actions.className = "favorite-actions";
    const loadButton = document.createElement("button");
    loadButton.className = "btn btn-outline-info btn-sm";
    loadButton.type = "button";
    loadButton.textContent = "Load";
    loadButton.addEventListener("click", () => applyFavorite(favorite));
    const playButton = document.createElement("button");
    playButton.className = "btn btn-outline-light btn-sm";
    playButton.type = "button";
    playButton.textContent = "Play";
    playButton.addEventListener("click", async () => {
      applyFavorite(favorite, { play: false });
      await playCurrentChord();
    });
    const deleteButton = document.createElement("button");
    deleteButton.className = "btn btn-outline-danger btn-sm";
    deleteButton.type = "button";
    deleteButton.textContent = "Delete";
    deleteButton.addEventListener("click", () => deleteFavorite(favorite.id, deleteButton));
    actions.append(loadButton, playButton, deleteButton);
    row.append(detail, actions);
    list.append(row);
  });
}

async function loadFavorites() {
  state.favoriteLoading = true;
  setFavoritesMessage("info", "Loading favorites...");
  try {
    const body = await requestJson("/chords/favorites");
    state.favorites = body.favorites || [];
    renderFavorites();
  } catch (error) {
    state.favorites = [];
    setFavoritesMessage("secondary", error.message);
  } finally {
    state.favoriteLoading = false;
  }
}

function readQueryState() {
  const params = new URLSearchParams(window.location.search);
  const root = params.get("root");
  const quality = params.get("quality");
  const chordTypeParam = params.get("chordType");
  const typeParam = params.get("type");
  const soundBank = params.get("soundBank");
  if (KEYS.includes(root)) {
    state.root = root;
  }
  if (isAllowedType(typeParam)) {
    state.type = typeParam;
  } else if (quality || chordTypeParam) {
    state.type = typeFromSavedChord(quality, chordTypeParam);
  }
  if (soundBank) {
    state.soundBank = normalizeSoundfontId(soundBank, state.soundBank);
  }
}

function init() {
  loadSettings();
  readQueryState();
  renderKeySelect();
  renderTypes();
  renderSoundBankOptions($("soundBankSelect"), state.soundBank);
  renderPiano($("piano"), (midi, element) => {
    const pitch = element.dataset.pitch;
    flashPianoKey($("piano"), midi);
    flashGuitarPitch($("guitarDiagram"), pitch);
    playSingleMidi(midi, element);
  });
  $("keySelect").addEventListener("change", (event) => {
    state.root = event.target.value;
    state.voicingIndex = 0;
    updateChord({ play: true });
  });
  $("advancedTypesButton").addEventListener("click", () => {
    state.showAdvancedTypes = !state.showAdvancedTypes;
    renderTypes();
  });
  $("soundBankSelect").addEventListener("change", (event) => {
    state.soundBank = normalizeSoundfontId(event.target.value);
    playSoundfontPreview("fatboy", state.soundBank);
    saveSettings();
  });
  $("prevVoicingButton").addEventListener("click", () => {
    state.voicingIndex -= 1;
    updateChord({ play: true });
  });
  $("nextVoicingButton").addEventListener("click", () => {
    state.voicingIndex += 1;
    updateChord({ play: true });
  });
  $("replayButton").addEventListener("click", () => playCurrentChord());
  $("saveFavoriteButton").addEventListener("click", () => saveFavorite());
  $("refreshFavoritesButton").addEventListener("click", () => loadFavorites());
  updateChord();
  loadFavorites();
}

init();
